import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/lib/api';

export type TemplateType = 'contract' | 'receipt' | 'amendment';
export type TemplateLanguage = 'ru' | 'uz' | 'en';

export type DocumentTemplate = {
  id: string;
  code: string;
  name: string;
  type: TemplateType;
  language: TemplateLanguage;
  bodyHtml: string;
  version: number;
  projectId: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
};

export type CreateDocumentTemplateInput = {
  code: string;
  name: string;
  type: TemplateType;
  language: TemplateLanguage;
  bodyHtml: string;
  projectId?: string;
  active?: boolean;
};

export type UpdateDocumentTemplateInput = Partial<CreateDocumentTemplateInput>;

export function useDocumentTemplates() {
  return useQuery({
    queryKey: ['document-templates'],
    queryFn: async () => {
      const res = await api.get<{ data: DocumentTemplate[] }>('/document-templates');
      return res.data.data;
    },
  });
}

export function useDocumentTemplate(id: string | undefined) {
  return useQuery({
    queryKey: ['document-template', id],
    enabled: !!id,
    queryFn: async () => {
      const res = await api.get<{ data: DocumentTemplate }>(`/document-templates/${id}`);
      return res.data.data;
    },
  });
}

export function useCreateDocumentTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateDocumentTemplateInput) => {
      const res = await api.post<{ data: DocumentTemplate }>('/document-templates', input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['document-templates'] });
    },
  });
}

export function useUpdateDocumentTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateDocumentTemplateInput }) => {
      const res = await api.patch<{ data: DocumentTemplate }>(`/document-templates/${id}`, input);
      return res.data.data;
    },
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: ['document-templates'] });
      void qc.invalidateQueries({ queryKey: ['document-template', vars.id] });
    },
  });
}

export function usePreviewInline() {
  return useMutation({
    mutationFn: async ({ bodyHtml, type }: { bodyHtml: string; type?: TemplateType }) => {
      const res = await api.post<{ data: { html: string; placeholders: string[] } }>(
        '/document-templates/preview-inline',
        { bodyHtml, type },
      );
      return res.data.data;
    },
  });
}
